<template>
  <div class="print-container p-4 md:p-6 max-w-[1600px] mx-auto">
    <!-- Header -->
    <div class="dashboard-panel !bg-gradient-to-br !from-header !to-slate-700 !text-white !border-slate-600 mb-6 no-print relative z-20">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div class="flex items-center gap-2 mb-1">
            <svg class="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h1 class="text-2xl md:text-3xl font-bold">Development Portfolio Executive Dashboard</h1>
          </div>
          <p class="text-blue-100 text-sm">Real-time visibility into portfolio health and risks</p>
        </div>
        <div class="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div v-if="developments.length > 0" class="flex items-center gap-2">
            <span class="text-sm text-blue-100 whitespace-nowrap">Development:</span>
            <DevelopmentFilter
              :developments="developments"
              :selectedDevelopments="selectedDevelopments"
              @update:selectedDevelopments="onDevelopmentChange"
            />
          </div>
          <div v-if="allWorkstreams.length > 0" class="flex items-center gap-2">
            <span class="text-sm text-blue-100 whitespace-nowrap">Workstreams:</span>
            <WorkstreamFilter
              :workstreams="allWorkstreams"
              :hiddenWorkstreams="hiddenWorkstreams"
              @update:hiddenWorkstreams="hiddenWorkstreams = $event"
            />
          </div>
          <span v-if="data" class="text-sm text-blue-100">
            Last updated: {{ formatDate(data.last_updated) }}
          </span>
          <button
            @click="handleDownloadPdf"
            :disabled="exporting || loading || !data"
            class="h-9 px-4 bg-white/10 hover:bg-white/20 text-white rounded-lg disabled:opacity-50 text-sm font-medium flex items-center gap-2 border border-white/20 transition-colors"
          >
            <svg v-if="exporting" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {{ exporting ? 'Exporting...' : 'Download PDF' }}
          </button>
          <button
            @click="handleRefresh"
            :disabled="refreshing || loading"
            class="h-9 px-4 bg-white/10 hover:bg-white/20 text-white rounded-lg disabled:opacity-50 text-sm font-medium flex items-center gap-2 border border-white/20 transition-colors"
          >
            <svg v-if="refreshing" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ refreshing ? 'Refreshing...' : 'Refresh' }}
          </button>
          <button
            v-if="isAdmin"
            @click="showManageUsers = true"
            class="h-9 px-4 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium flex items-center gap-2 border border-white/20 transition-colors"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Users
          </button>
          <button
            @click="$emit('logout')"
            class="h-9 px-4 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium flex items-center gap-2 border border-white/20 transition-colors"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </div>
    </div>

    <!-- Print Header -->
    <div class="hidden print-only mb-4">
      <h1 class="text-2xl font-bold text-header">Development Portfolio Executive Dashboard</h1>
      <p class="text-sm text-gray-500">Generated: {{ new Date().toLocaleString() }}</p>
    </div>

    <!-- Loading / Error -->
    <div v-if="loading && !data" class="text-center py-12">
      <p class="text-gray-500">Loading dashboard...</p>
    </div>
    <div v-if="error" class="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
      <p>{{ error }}</p>
    </div>

    <!-- Dashboard Content -->
    <div v-if="data" class="space-y-6">
      <!-- Top Metric Cards -->
      <div data-pdf-section class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard label="Total Developments" :value="filteredSummary.total_developments" />
        <MetricCard label="Total Workstreams" :value="filteredSummary.total_workstreams" />
        <MetricCard label="On Track" :value="filteredSummary.on_track" type="on-track" />
        <MetricCard label="At Risk" :value="filteredSummary.at_risk" type="at-risk" />
        <MetricCard label="Devs with Delayed Workstreams" :value="filteredSummary.delayed" type="delayed" />
        <MetricCard label="Delayed Workstreams" :value="filteredSummary.total_delayed_workstreams" type="delayed" />
      </div>

      <!-- Health + Status + Highlights -->
      <div data-pdf-section class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <HealthGauge :score="filteredHealthScore" :label="filteredHealthLabel" />
        <StatusDonut
          :on-track="filteredSummary.on_track"
          :at-risk="filteredSummary.at_risk"
          :delayed="filteredSummary.total_delayed_workstreams"
        />
        <AlertSection :risks="data.risks" />
      </div>

      <!-- Gantt Chart -->
      <GanttChart data-pdf-section :items="filteredTimeline" />

      <!-- Capital Raises Chart -->
      <CapitalRaisesChart 
        data-pdf-section
        :raises="filteredRaises" 
        :loading="raisesLoading" 
        :error="raisesError" 
      />

      <!-- Forecast Finish Editor -->
      <ForecastFinishTable :items="filteredTimeline" @updated="fetchData" />

      <!-- Unscheduled Workstreams -->
      <UnscheduledTable data-pdf-section data-pdf-split :items="filteredUnscheduled" />

      <!-- Development Health Table -->
      <div data-pdf-section class="dashboard-panel print-break-inside-avoid">
        <h3 class="section-title mb-4">
          <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Development Health
        </h3>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="table-header">
              <tr>
                <th class="px-3 py-2">Development</th>
                <th class="px-3 py-2">Health Score</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2">Delayed Workstreams</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in filteredDevelopmentSummary"
                :key="item.development"
                class="border-b border-gray-100 hover:bg-gray-50"
              >
                <td class="px-3 py-2 font-medium">{{ item.development }}</td>
                <td class="px-3 py-2">
                  <span class="font-bold" :class="scoreColor(item.health_score)">
                    {{ item.health_score }}/100
                  </span>
                </td>
                <td class="px-3 py-2">
                  <span class="inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-semibold" :class="statusBadge(item.status)">
                    <span class="w-3 h-3 rounded-full" :class="statusDot(item.status)"></span>
                    {{ item.status }}
                  </span>
                </td>
                <td class="px-3 py-2">{{ item.delayed_workstreams }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Risk Table -->
      <RiskTable data-pdf-section data-pdf-split :risks="data.risks" />

      <!-- Capital Raises Table -->
      <CapitalRaisesTable 
        data-pdf-section
        data-pdf-split
        :raises="filteredRaises" 
        :loading="raisesLoading" 
        :error="raisesError" 
        :last-updated="raisesLastUpdated" 
      />
    </div>

    <ManageUsers v-if="showManageUsers" @close="showManageUsers = false" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useDashboardData } from '../composables/useDashboardData.js'
import { useAuth } from '../composables/useAuth.js'
import MetricCard from '../components/MetricCard.vue'
import HealthGauge from '../components/HealthGauge.vue'
import StatusDonut from '../components/StatusDonut.vue'
import GanttChart from '../components/GanttChart.vue'
import RiskTable from '../components/RiskTable.vue'
import AlertSection from '../components/AlertSection.vue'
import UnscheduledTable from '../components/UnscheduledTable.vue'
import WorkstreamFilter from '../components/WorkstreamFilter.vue'
import DevelopmentFilter from '../components/DevelopmentFilter.vue'
import CapitalRaisesTable from '../components/CapitalRaisesTable.vue'
import CapitalRaisesChart from '../components/CapitalRaisesChart.vue'
import ForecastFinishTable from '../components/ForecastFinishTable.vue'
import ManageUsers from '../views/ManageUsers.vue'
import { useCapitalRaises } from '../composables/useCapitalRaises.js'
import { exportDashboardPdf } from '../composables/usePdfExport.js'
import { api } from '../services/api.js'

const emit = defineEmits(['logout'])

const { user } = useAuth()
const isAdmin = computed(() => user.value?.is_admin === true)

const { data, loading, error, developments, fetchData, setDevelopment } = useDashboardData()
const { raises, loading: raisesLoading, error: raisesError, lastUpdated: raisesLastUpdated, setDevelopment: setRaisesDevelopment } = useCapitalRaises()

// Multi-select: all checked by default once developments load
const selectedDevelopments = ref(new Set())

watch(
  () => developments.value,
  (devs) => {
    if (devs.length > 0 && selectedDevelopments.value.size === 0) {
      selectedDevelopments.value = new Set(devs)
    }
  },
  { immediate: true }
)

const refreshing = ref(false)
const exporting = ref(false)
const showManageUsers = ref(false)

const pdfDevLabel = computed(() => {
  const allSelected = selectedDevelopments.value.size === developments.value.length
  if (allSelected || selectedDevelopments.value.size === 0) return 'All'
  return Array.from(selectedDevelopments.value).sort().join(', ')
})

const handleDownloadPdf = async () => {
  exporting.value = true
  try {
    await exportDashboardPdf(pdfDevLabel.value)
  } catch (e) {
    console.error('PDF export failed:', e)
    alert(`PDF export failed: ${e.message}`)
  } finally {
    exporting.value = false
  }
}

const handleRefresh = async () => {
  refreshing.value = true
  try {
    await api.post('/dashboard/refresh')
  } catch (e) {
    // proceed even if refresh endpoint fails
  }
  // Pass null for no filter (all), or fetch without filter and filter client-side
  await Promise.all([fetchData(), setRaisesDevelopment(null)])
  refreshing.value = false
}

const onDevelopmentChange = (newSelection) => {
  selectedDevelopments.value = newSelection
  hiddenWorkstreams.value = new Set()
}

const hiddenWorkstreams = ref(new Set())

const allWorkstreams = computed(() => {
  if (!data.value) return []
  const names = new Set([
    ...(data.value.timeline || []).map(i => i.workstream),
    ...(data.value.unscheduled || []).map(i => i.workstream)
  ])
  return Array.from(names).sort()
})

// Filter timeline and unscheduled by selected developments and hidden workstreams
const activeDevelopments = computed(() => {
  const allSelected = selectedDevelopments.value.size === developments.value.length
  return allSelected ? null : selectedDevelopments.value
})

const filteredTimeline = computed(() =>
  (data.value?.timeline || []).filter(i =>
    !hiddenWorkstreams.value.has(i.workstream) &&
    (!activeDevelopments.value || activeDevelopments.value.has(i.development))
  )
)

const filteredUnscheduled = computed(() =>
  (data.value?.unscheduled || []).filter(i =>
    !hiddenWorkstreams.value.has(i.workstream) &&
    (!activeDevelopments.value || activeDevelopments.value.has(i.development))
  )
)

// Filter raises by selected developments (client-side)
const filteredRaises = computed(() => {
  if (!activeDevelopments.value) return raises.value
  return (raises.value || []).filter(r => activeDevelopments.value.has(r.development))
})

// Compute filtered metrics from filteredTimeline
const filteredSummary = computed(() => {
  const all = [...filteredTimeline.value, ...filteredUnscheduled.value]
  const devs = new Set(all.map(i => i.development))
  const onTrack = filteredTimeline.value.filter(i => i.status === 'On Track').length
  const atRisk = filteredTimeline.value.filter(i => i.status === 'At Risk').length
  const delayedWorkstreams = filteredTimeline.value.filter(i => i.status === 'Delayed').length
  const delayedDevs = new Set(
    filteredTimeline.value.filter(i => i.status === 'Delayed').map(i => i.development)
  ).size
  return {
    total_developments: devs.size,
    total_workstreams: filteredTimeline.value.length + filteredUnscheduled.value.length,
    on_track: onTrack,
    at_risk: atRisk,
    delayed: delayedDevs,
    total_delayed_workstreams: delayedWorkstreams
  }
})

// Compute filtered development health summary
const filteredDevelopmentSummary = computed(() => {
  if (!data.value) return []
  if (!activeDevelopments.value) return data.value.development_summary || []
  return (data.value.development_summary || []).filter(d => activeDevelopments.value.has(d.development))
})

// Compute filtered health score from filtered development summary
const filteredHealthScore = computed(() => {
  const devs = filteredDevelopmentSummary.value
  if (!devs.length) return data.value?.portfolio_health || 0
  return Math.round(devs.reduce((sum, d) => sum + d.health_score, 0) / devs.length)
})

const filteredHealthLabel = computed(() => {
  const score = filteredHealthScore.value
  if (score >= 80) return 'On Track'
  if (score >= 60) return 'At Risk'
  return 'Delayed'
})

const formatDate = (value) => {
  if (!value) return '-'
  return new Date(value).toLocaleString()
}

const scoreColor = (score) => {
  if (score >= 80) return 'text-on-track'
  if (score >= 60) return 'text-at-risk'
  return 'text-delayed'
}

const statusBadge = (status) => {
  switch (status) {
    case 'On Track': return 'bg-green-100 text-green-800'
    case 'At Risk': return 'bg-yellow-100 text-yellow-800'
    case 'Delayed': return 'bg-red-100 text-red-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

const statusDot = (status) => {
  switch (status) {
    case 'On Track': return 'bg-green-500'
    case 'At Risk': return 'bg-yellow-500'
    case 'Delayed': return 'bg-red-500'
    default: return 'bg-gray-500'
  }
}
</script>

<style scoped>
@media print {
  .print-only {
    display: block !important;
  }
}
</style>
